import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as topojsonClient from "topojson-client";
import { geoMercator, geoPath } from "d3-geo";
import useMapZoom from "@/hooks/useMapZoom";
import MapZoomControls from "@/components/MapZoomControls";

const TOPO_TO_APP = {
  "Andaman & Nicobar Island": "Andaman & Nicobar Islands",
  "Arunanchal Pradesh": "Arunachal Pradesh",
  "Dadara & Nagar Havelli": "Dadra & Nagar Haveli and Daman & Diu",
  "Daman & Diu": "Dadra & Nagar Haveli and Daman & Diu",
  "NCT of Delhi": "Delhi",
  "Orissa": "Odisha",
};
const canonical = (name) => (name ? TOPO_TO_APP[name] || name : null);

// Offset labels for small/adjacent states to avoid overlap with larger neighbours.
// Values are [dx, dy] added to the state centroid.
const LABEL_OFFSETS = {
  "Goa": [-56, 22],
  "Delhi": [-58, -10],
  "Chandigarh": [-60, -8],
  "Puducherry": [70, 8],
  "Sikkim": [-52, -18],
  "Dadra & Nagar Haveli and Daman & Diu": [-64, 10],
  "Andaman & Nicobar Islands": [-60, 0],
  "Lakshadweep": [-58, 0],
  "Tripura": [-46, 18],
  "Meghalaya": [-40, -22],
  "Manipur": [46, 6],
  "Nagaland": [46, -14],
  "Mizoram": [46, 20],
};

/**
 * Interactive India Map. Colours states by:
 *   - Operational (has operational hotels) → brand blue
 *   - Upcoming only (only upcoming hotels) → brand orange
 *   - No hotels → grey
 */
export default function IndiaMap({ activeStates = {}, hotels = [], height = 620 }) {
  const [topo, setTopo] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [hoveredCluster, setHoveredCluster] = useState(null);
  const [openCluster, setOpenCluster] = useState(null);
  const [cursor, setCursor] = useState({ x: 20, y: 20 });
  const containerRef = useRef(null);
  const [width, setWidth] = useState(800);
  const navigate = useNavigate();
  const zoomCtl = useMapZoom({ width, height });

  useEffect(() => {
    fetch("/india-states.json").then((r) => r.json()).then(setTopo).catch(() => setTopo(null));
  }, []);

  useEffect(() => {
    const updateWidth = () => containerRef.current && setWidth(containerRef.current.clientWidth);
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  const { paths, viewBox, projection } = useMemo(() => {
    if (!topo) return { paths: [], viewBox: `0 0 ${width} ${height}`, projection: null };
    const objectKey = Object.keys(topo.objects)[0];
    const geo = topojsonClient.feature(topo, topo.objects[objectKey]);
    const proj = geoMercator().fitSize([width, height], geo);
    const pathGen = geoPath(proj);
    const list = geo.features
      .map((f) => {
        const rawName = f.properties?.name || null;
        const name = canonical(rawName);
        return { name, rawName, d: pathGen(f), centroid: pathGen.centroid(f) };
      })
      .filter((p) => p.d && p.name);
    return { paths: list, viewBox: `0 0 ${width} ${height}`, projection: proj };
  }, [topo, width, height]);

  // Project hotels with valid coordinates and cluster by (state, city)
  const clusters = useMemo(() => {
    if (!projection || !hotels?.length) return [];
    const groups = new Map();
    for (const h of hotels) {
      if (
        typeof h.latitude !== "number" ||
        typeof h.longitude !== "number" ||
        !Number.isFinite(h.latitude) ||
        !Number.isFinite(h.longitude)
      ) continue;
      const p = projection([h.longitude, h.latitude]);
      if (!p) continue;
      const key = `${h.state}::${h.city}`;
      const g = groups.get(key) || {
        key,
        state: h.state,
        city: h.city,
        x: p[0],
        y: p[1],
        hotels: [],
        operational: 0,
        upcoming: 0,
      };
      g.hotels.push(h);
      if (h.status === "upcoming") g.upcoming += 1;
      else g.operational += 1;
      // Recompute cluster centre as running average
      g.x = (g.x * (g.hotels.length - 1) + p[0]) / g.hotels.length;
      g.y = (g.y * (g.hotels.length - 1) + p[1]) / g.hotels.length;
      groups.set(key, g);
    }
    return [...groups.values()];
  }, [hotels, projection]);

  return (
    <div
      ref={containerRef}
      data-testid="india-map"
      className="relative w-full bg-[var(--paper-2)] border border-[var(--ink)] touch-none"
      style={{ minHeight: height, cursor: zoomCtl.zoom > 1 ? "grab" : "default" }}
      onMouseMove={(e) => {
        const rect = containerRef.current?.getBoundingClientRect();
        if (rect) setCursor({ x: e.clientX - rect.left, y: e.clientY - rect.top });
      }}
      {...zoomCtl.pointerHandlers}
    >
      {!topo ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="font-label text-xs text-[var(--ink-soft)]">Loading map…</div>
        </div>
      ) : (
        <svg viewBox={zoomCtl.viewBox} width="100%" height={height} preserveAspectRatio="xMidYMid meet">
          <defs>
            <filter id="dotShadow" x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow dx="0" dy="1" stdDeviation="1" floodOpacity="0.25" />
            </filter>
          </defs>
          {/* State polygons — neutral grey, only the dots carry colour */}
          {paths.map((p, i) => {
            const stats = activeStates[p.name];
            const active = stats?.hotel_count > 0;
            const isHovered = hovered === p.name;
            const fill = isHovered ? "#D1D5DB" : active ? "#EEF0F3" : "#F3F4F6";
            const stroke = active ? "#9CA3AF" : "#D1D5DB";
            return (
              <path
                key={`${p.name}-${i}`}
                d={p.d}
                fill={fill}
                stroke={stroke}
                strokeWidth={0.7}
                data-testid={`map-state-${slug(p.name)}`}
                onMouseEnter={() => setHovered(p.name)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => { if (!zoomCtl.suppressClick()) navigate(`/state/${encodeURIComponent(p.name)}`); }}
                style={{ cursor: "pointer", transition: "fill 200ms" }}
              />
            );
          })}
          {/* City clusters — one marker per city with a count badge if multiple hotels */}
          {clusters.map((c) => {
            const total = c.hotels.length;
            const mixed = c.operational > 0 && c.upcoming > 0;
            const primary = c.operational >= c.upcoming ? "#0047FF" : "#FF5A36";
            const secondary = c.operational >= c.upcoming ? "#FF5A36" : "#0047FF";
            // Radius scales gently with count for touch legibility (min 12 → up to 22)
            const r = total === 1 ? 10 : Math.min(22, 12 + Math.log2(total) * 3);
            return (
              <g
                key={c.key}
                data-testid={`map-cluster-${slug(c.city)}`}
                style={{ cursor: "pointer" }}
                onMouseEnter={() => setHoveredCluster(c)}
                onMouseLeave={() => setHoveredCluster(null)}
                onClick={() => {
                  if (zoomCtl.suppressClick()) return;
                  if (total === 1) {
                    navigate(`/state/${encodeURIComponent(c.state)}/city/${encodeURIComponent(c.city)}`);
                  } else {
                    setOpenCluster(c);
                  }
                }}
              >
                {/* Invisible larger hit area for touch — ~44px diameter min */}
                <circle cx={c.x} cy={c.y} r={Math.max(r + 12, 22)} fill="transparent" />
                {mixed ? (
                  <>
                    {/* Split hemisphere: left = primary, right = secondary */}
                    <path
                      d={`M ${c.x} ${c.y - r} A ${r} ${r} 0 0 0 ${c.x} ${c.y + r} Z`}
                      fill={primary}
                    />
                    <path
                      d={`M ${c.x} ${c.y - r} A ${r} ${r} 0 0 1 ${c.x} ${c.y + r} Z`}
                      fill={secondary}
                    />
                    <circle
                      cx={c.x}
                      cy={c.y}
                      r={r}
                      fill="none"
                      stroke="white"
                      strokeWidth={2.5}
                      filter="url(#dotShadow)"
                    />
                  </>
                ) : (
                  <circle
                    cx={c.x}
                    cy={c.y}
                    r={r}
                    fill={primary}
                    stroke="white"
                    strokeWidth={2.5}
                    filter="url(#dotShadow)"
                  />
                )}
                {total > 1 && (
                  <text
                    x={c.x}
                    y={c.y + 4}
                    textAnchor="middle"
                    fontSize={r >= 18 ? 12 : 11}
                    fontWeight={700}
                    fill="white"
                    pointerEvents="none"
                    style={{ fontFamily: "Space Grotesk, sans-serif" }}
                  >
                    {total}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      )}

      {/* Cluster hover tooltip (quick summary) */}
      {hoveredCluster && !openCluster && (
        <div
          data-testid="map-cluster-tooltip"
          className="pointer-events-none absolute bg-[var(--ink)] text-white px-4 py-3 border border-[var(--ink)] z-30 shadow-[6px_6px_0_0_var(--brand-orange)]"
          style={{
            left: Math.min(cursor.x + 16, (containerRef.current?.clientWidth || 800) - 260),
            top: Math.max(cursor.y - 74, 8),
          }}
        >
          <div className="font-label text-[10px] text-[var(--brand-yellow)] mb-1">
            {hoveredCluster.city}, {hoveredCluster.state}
          </div>
          <div className="font-display text-lg leading-none">
            {hoveredCluster.hotels.length} {hoveredCluster.hotels.length === 1 ? "hotel" : "hotels"}
          </div>
          <div className="text-xs mt-2 text-white/80 flex items-center gap-3">
            {hoveredCluster.operational > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-[var(--brand-blue)]" />
                {hoveredCluster.operational} operational
              </span>
            )}
            {hoveredCluster.upcoming > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-[var(--brand-orange)]" />
                {hoveredCluster.upcoming} upcoming
              </span>
            )}
          </div>
          <div className="text-[10px] mt-2 text-white/50">
            {hoveredCluster.hotels.length > 1 ? "Click to choose hotel" : "Click to open"}
          </div>
        </div>
      )}

      {/* Cluster popup — appears when a multi-hotel cluster is clicked */}
      {openCluster && (
        <div
          data-testid="map-cluster-popup"
          className="absolute inset-0 z-40 bg-black/20 flex items-center justify-center p-4"
          onClick={() => setOpenCluster(null)}
        >
          <div
            className="bg-white border border-[var(--ink)] shadow-[8px_8px_0_0_var(--ink)] w-full max-w-md max-h-[80%] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 sm:p-5 border-b border-[var(--line)] flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-label text-[10px] text-[var(--brand-blue)] mb-1">
                  {openCluster.state}
                </div>
                <h3 className="font-display text-2xl sm:text-3xl leading-tight truncate">
                  {openCluster.city}
                </h3>
                <div className="text-xs text-[var(--ink-soft)] mt-1 flex items-center gap-3 flex-wrap">
                  {openCluster.operational > 0 && (
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-[var(--brand-blue)]" />
                      {openCluster.operational} operational
                    </span>
                  )}
                  {openCluster.upcoming > 0 && (
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-[var(--brand-orange)]" />
                      {openCluster.upcoming} upcoming
                    </span>
                  )}
                </div>
              </div>
              <button
                data-testid="map-cluster-popup-close"
                onClick={() => setOpenCluster(null)}
                className="shrink-0 p-2 hover:bg-[var(--paper-2)]"
                aria-label="Close"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="overflow-y-auto flex-1">
              {openCluster.hotels.map((h) => {
                const upcoming = h.status === "upcoming";
                return (
                  <button
                    key={h.id}
                    data-testid={`map-cluster-hotel-${h.id}`}
                    onClick={() => {
                      setOpenCluster(null);
                      navigate(`/state/${encodeURIComponent(h.state)}/city/${encodeURIComponent(h.city)}`);
                    }}
                    className="w-full flex items-center gap-3 px-4 sm:px-5 py-4 hover:bg-[var(--paper-2)] text-left border-b border-[var(--line)] last:border-0"
                  >
                    <span
                      className={`w-3 h-3 rounded-full shrink-0 ${
                        upcoming ? "bg-[var(--brand-orange)]" : "bg-[var(--brand-blue)]"
                      }`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-base truncate">{h.name}</div>
                      <div className="text-xs text-[var(--ink-soft)] truncate">
                        {h.location} · {h.rooms} rooms
                      </div>
                    </div>
                    <span
                      className={`font-label text-[9px] px-2 py-0.5 shrink-0 ${
                        upcoming
                          ? "bg-[var(--brand-orange)] text-white"
                          : "bg-[var(--paper-3)] text-[var(--ink)]"
                      }`}
                    >
                      {upcoming ? "Upcoming" : "Live"}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="p-3 border-t border-[var(--line)] bg-[var(--paper-2)] text-right">
              <button
                data-testid="map-cluster-popup-view-city"
                onClick={() => {
                  const c = openCluster;
                  setOpenCluster(null);
                  navigate(`/state/${encodeURIComponent(c.state)}/city/${encodeURIComponent(c.city)}`);
                }}
                className="inline-flex items-center gap-2 bg-[var(--ink)] text-white px-4 py-2 font-display text-xs hover:bg-[var(--brand-blue)]"
              >
                Open {openCluster.city} page →
              </button>
            </div>
          </div>
        </div>
      )}

      <MapZoomControls
        zoom={zoomCtl.zoom}
        onZoomIn={zoomCtl.zoomIn}
        onZoomOut={zoomCtl.zoomOut}
        onReset={zoomCtl.reset}
        testIdPrefix="india-map-zoom"
      />

      {hovered && !hoveredCluster && !openCluster && (
        <div
          data-testid="map-tooltip"
          className="pointer-events-none absolute bg-[var(--ink)] text-white px-4 py-3 border border-[var(--ink)] z-20 shadow-[6px_6px_0_0_var(--brand-blue)]"
          style={{
            left: Math.min(cursor.x + 16, (containerRef.current?.clientWidth || 800) - 240),
            top: Math.max(cursor.y - 60, 8),
          }}
        >
          <div className="font-label text-[10px] text-[var(--brand-yellow)] mb-1">State</div>
          <div className="font-display text-lg leading-none">{hovered}</div>
          {activeStates[hovered] ? (
            <div className="text-xs mt-2 text-white/80">
              {activeStates[hovered].hotel_count} {activeStates[hovered].hotel_count === 1 ? "hotel" : "hotels"}
              {" · "}
              {activeStates[hovered].city_count} {activeStates[hovered].city_count === 1 ? "city" : "cities"}
              {activeStates[hovered].upcoming > 0 && (
                <div className="mt-1 text-[var(--brand-yellow)]">
                  {activeStates[hovered].upcoming} upcoming
                </div>
              )}
            </div>
          ) : (
            <div className="text-xs mt-2 text-white/60">No hotels yet</div>
          )}
        </div>
      )}

      <div className="absolute bottom-3 right-3 bg-white border border-[var(--ink)] px-3 py-2 flex items-center gap-3 sm:gap-4 text-[11px] font-label">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-[var(--brand-blue)] border-2 border-white" style={{ boxShadow: "0 0 0 1px #0047FF" }} />
          <span>Operational</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-[var(--brand-orange)] border-2 border-white" style={{ boxShadow: "0 0 0 1px #FF5A36" }} />
          <span>Upcoming</span>
        </div>
        <div className="flex items-center gap-2">
          <span
            className="w-3 h-3 rounded-full border-2 border-white"
            style={{ background: "linear-gradient(90deg, #0047FF 50%, #FF5A36 50%)", boxShadow: "0 0 0 1px #111827" }}
          />
          <span>Mixed</span>
        </div>
      </div>
    </div>
  );
}

function slug(s) {
  return (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}
